import { useState, useEffect } from "react";
import {
  cancelStripeSubscription,
  getStripeSubscriptionHistory,
} from "../api/apiFunctions";
import { toast } from "react-toastify";

function formatDate(dateStr) {
  const d = new Date(dateStr);
  return d.toLocaleDateString();
}

const SubscriptionHistory = () => {
  const [activeSubs, setActiveSubs] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isCancellingSubscription, setIsCancellingSubscription] =
    useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const token =
          localStorage.getItem("authToken") ||
          sessionStorage.getItem("authToken");
        const res = await getStripeSubscriptionHistory(token);
        if (res && res.subscriptions && Array.isArray(res.subscriptions)) {
          const activeSub = res.subscriptions.find((s) => s.isActive);
          setActiveSubs(activeSub || null);
          const allHistory = res.subscriptions.flatMap((s) => s.history || []);
          allHistory.sort(
            (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
          );
          setHistory(allHistory);
        }
      } catch (err) {
        setError(err.message || "Failed to fetch subscription history");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleCancelSubscription = async () => {
    const token =
      localStorage.getItem("authToken") || sessionStorage.getItem("authToken");
    try {
      setIsCancellingSubscription(true);
      const response = await cancelStripeSubscription(
        {
          subscriptionId: localStorage.getItem("activeSubscriptionId"),
        },
        token,
      );
      if (response.success) {
        toast.success(
          "Subscription canceled successfully. But you can still use it until the end of the billing period.",
        );
        // the user can still use the app and once the backend returns status = cancellled, the ui will show the
        // expired ui automatically.
      }
    } catch (error) {
      console.error("Cancel subscription error:", error);
      toast.error(error || "Failed to cancel subscription");
    } finally {
      setIsCancellingSubscription(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto p-8 text-center text-blue-700 font-semibold text-lg">
        Loading subscription history...
      </div>
    );
  }
  if (error) {
    return (
      <div className="max-w-2xl mx-auto p-8 text-center text-red-600 font-semibold text-lg">
        {error}
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto p-4">
      <h2 className="md:text-2xl text-xl font-bold mb-6 text-blue-900">
        Subscription History
      </h2>

      {/* Active Subscription Card */}
      {activeSubs ? (
        <ActiveSubscriptionCard
          activeSubs={activeSubs}
          onCancel={handleCancelSubscription}
          isCancellingSubscription={isCancellingSubscription}
        />
      ) : (
        <div className="bg-white rounded-xl shadow p-6 mb-8 text-gray-500 text-center">
          No active subscription.
        </div>
      )}

      {/* Subscription History */}
      <div>
        <h3 className="text-xl font-semibold mb-4 text-blue-800">History</h3>
        {history.length === 0 ? (
          <div className="bg-gray-50 rounded-lg p-4 text-gray-500 text-center">
            No history found.
          </div>
        ) : (
          <div className="space-y-4">
            {history.map((h) => {
              const isFailed = h.paymentStatus === "failed";
              return (
                <SubscriptionHistoryCard
                  key={h._id}
                  history={h}
                  isFailed={isFailed}
                />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionHistory;

const SubscriptionHistoryCard = ({ history, isFailed }) => {
  return (
    <div
      className={`bg-white border rounded-lg p-4 shadow flex flex-col md:flex-row md:items-center justify-between ${isFailed ? "border-red-400" : "border-gray-200"}`}
    >
      <div>
        <div
          className={`font-medium mb-1 ${isFailed ? "text-red-700" : "text-blue-700"}`}
        >
          {history.eventType
            .replace(/_/g, " ")
            .replace(/\b\w/g, (l) => l.toUpperCase())}
        </div>
        <div
          className={`text-sm mb-1 ${isFailed ? "text-red-600" : "text-gray-600"}`}
        >
          {history.description}
        </div>
        <div className="text-xs text-gray-400">
          {formatDate(history.createdAt)}
        </div>
        {isFailed && history.failureReason && (
          <div className="text-xs text-red-500 mt-1 font-semibold">
            Reason: {history.failureReason}
          </div>
        )}
      </div>
      {history.amount && !isFailed && (
        <div className="text-green-700 font-semibold text-lg mt-2 md:mt-0 md:ml-8">
          +${(history.amount / 100).toFixed(2)}
        </div>
      )}
      {isFailed && (
        <div className="text-red-700 font-semibold text-lg mt-2 md:mt-0 md:ml-8">
          Payment Failed
        </div>
      )}
    </div>
  );
};

const ActiveSubscriptionCard = ({
  activeSubs,
  onCancel,
  isCancellingSubscription,
}) => {
  return (
    <div className="bg-white rounded-xl shadow-md p-6 mb-8 border border-blue-200 flex flex-col md:flex-row md:items-center justify-between">
      <div>
        <div className="text-lg font-semibold text-blue-800 mb-1">
          {activeSubs.packageId.name}
        </div>
        <div className="text-gray-600 mb-2">
          {activeSubs.packageId.description}
        </div>
        <div className="text-sm text-gray-500 mb-1">
          <span className="font-medium text-blue-700">Status:</span>{" "}
          {activeSubs.status.charAt(0).toUpperCase() +
            activeSubs.status.slice(1)}
        </div>
        <div className="text-sm text-gray-500 mb-1">
          <span className="font-medium text-blue-700">Period:</span>{" "}
          {formatDate(activeSubs.currentPeriodStart)} -{" "}
          {formatDate(activeSubs.currentPeriodEnd)}
        </div>
        <div className="text-sm text-gray-500">
          <span className="font-medium text-blue-700">Interval:</span>{" "}
          {activeSubs.interval.charAt(0).toUpperCase() +
            activeSubs.interval.slice(1)}
        </div>
      </div>
      <button
        className="mt-4 w-fit md:mt-0 md:ml-8 px-5 py-2 bg-red-500 hover:bg-red-700 text-white font-semibold rounded-lg shadow transition"
        onClick={onCancel}
        disabled={isCancellingSubscription}
      >
        <p className="whitespace-nowrap">
          {isCancellingSubscription ? "Cancelling..." : "Cancel Subscription"}
        </p>
      </button>
    </div>
  );
};
